import React, { useMemo, useState } from 'react';
import { Box, Slider, Stack, Tab, Tabs, Typography } from '@mui/material';
import { DriverDetails } from '../../types/predictions';
import { SimDriverResult, SimModel } from '../../services/api';
import { RaceTrendChart } from '../RaceTrendChart';
import { raceKey } from './chartKit';
import { FinishOddsChart } from './FinishOddsChart';
import { FinishSpreadChart } from './FinishSpreadChart';
import { HeadToHeadChart } from './HeadToHeadChart';
import { SpeedVsResultChart } from './SpeedVsResultChart';

interface Props {
  driversDetails: Record<string, DriverDetails[]>;
  /** The simulated track's key, for the trend chart's "This track" filter. */
  trackKey?: string;
  /** The Simulator's model; enables the Trend chart's "vs model" comparison. */
  model?: SimModel;
  /** The simulation's results for these drivers (the Finish odds tab), the whole field (its "typical" line), and
   * the run's simulation count. */
  finishOdds?: SimDriverResult[];
  field?: SimDriverResult[];
  nSims?: number;
}

type View = 'trend' | 'spread' | 'h2h' | 'speed' | 'odds';

/** Charts over the race-by-race data: trend, finish spread, head-to-head (2+ drivers) and speed vs result. */
export const RaceCharts: React.FC<Props> = ({ driversDetails, trackKey, model, finishOdds = [], field, nSims }) => {
  const multi = Object.keys(driversDetails).length > 1;
  const [view, setView] = useState<View>('trend');
  // Finish odds come from the simulation, so the tab only appears when it returned the per-position shares.
  // Ordered like the race data so each driver keeps the same color in every tab.
  const order = Object.keys(driversDetails);
  const odds = finishOdds
    .filter((d) => d.finishDist && d.finishDist.length > 0)
    .sort((a, b) => order.indexOf(a.driver) - order.indexOf(b.driver));
  const shown = (!multi && view === 'h2h') || (odds.length === 0 && view === 'odds') ? 'trend' : view;

  // Every distinct race across the drivers, newest first. The slider keeps the N most recent of these for everyone,
  // so drivers are always compared over the same races.
  const races = useMemo(() => {
    const seen = new Map<string, string>();
    Object.values(driversDetails).forEach((list) => list.forEach((r) => seen.set(raceKey(r), r.Date)));
    return Array.from(seen.entries())
      .sort((x, y) => (x[1] < y[1] ? 1 : x[1] > y[1] ? -1 : 0))
      .map(([k]) => k);
  }, [driversDetails]);
  const total = races.length;
  const minRaces = Math.min(5, total);
  // null = all races (follows the data when it changes).
  const [count, setCount] = useState<number | null>(null);
  const n = count == null ? total : Math.max(minRaces, Math.min(count, total));

  const recent = useMemo(() => {
    if (n >= total) return driversDetails;
    const keep = new Set(races.slice(0, n));
    return Object.fromEntries(
      Object.entries(driversDetails).map(([d, list]) => [d, list.filter((r) => keep.has(raceKey(r)))]),
    );
  }, [driversDetails, races, n, total]);

  if (Object.keys(driversDetails).length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Tabs
        value={shown}
        onChange={(_, v) => setView(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', py: 0.5 } }}
      >
        <Tab value="trend" label="Trend" />
        <Tab value="spread" label="Finish spread" />
        {multi && <Tab value="h2h" label="Head to head" />}
        <Tab value="speed" label="Speed vs result" />
        {odds.length > 0 && <Tab value="odds" label="Boom or bust" />}
      </Tabs>
      {/* The race slider filters past races; finish odds are the simulation's, so it doesn't apply there. */}
      {total > minRaces && shown !== 'odds' && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5, maxWidth: 520, pr: 1 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', minWidth: 120, fontVariantNumeric: 'tabular-nums' }}>
            {n >= total ? `All ${total} races` : `Last ${n} of ${total} races`}
          </Typography>
          <Slider
            size="small"
            aria-label="Races to show"
            min={minRaces}
            max={total}
            step={1}
            value={n}
            onChange={(_, v) => setCount((v as number) >= total ? null : (v as number))}
            valueLabelDisplay="auto"
          />
        </Stack>
      )}
      {shown === 'trend' && <RaceTrendChart driversDetails={recent} trackKey={trackKey} model={model} />}
      {shown === 'spread' && <FinishSpreadChart driversDetails={recent} />}
      {shown === 'h2h' && <HeadToHeadChart driversDetails={recent} />}
      {shown === 'speed' && <SpeedVsResultChart driversDetails={recent} />}
      {shown === 'odds' && (
        <FinishOddsChart drivers={odds} field={odds.length === 1 ? field : undefined} nSims={nSims} />
      )}
    </Box>
  );
};
